import { useEffect, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { collectFingerprint, verifyMagicLink, type UserView } from '../lib/api';
import { useAuth } from '../lib/auth';

// Confirm-on-click flow:
//
// The magic-link token is single-use. Some mail clients (Apple Mail's link
// preview, Gmail's anti-phishing scanner, corporate URL rewriters) issue a
// background GET to the link before the human ever taps it. If this page
// auto-consumes the token on mount, those bots burn the user's link and the
// real click then sees "already used".
//
// So we render a Confirm button instead and only POST /verify when the user
// explicitly clicks. Bots that load HTML+JS but never produce a real click
// stop consuming tokens.
//
// The OAuth round-trip case (Google/Apple landing back here with no token,
// just an httpOnly refresh cookie) is still automatic — there's no one-shot
// token to protect and the cookie is harmless to scanners.

type Stage = 'idle' | 'working' | 'done' | 'error';

export function AuthCallback() {
  const { t, localePath } = useI18n();
  const { hydrate } = useAuth();
  const [stage, setStage] = useState<Stage>('idle');
  const [msg, setMsg] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [requested, setRequested] = useState<string | null>(null);

  // Run once on mount: extract the URL state and either finish the OAuth
  // round-trip (no token in URL → backend already set bc_refresh) or wait
  // for the user to click the confirm button (token in URL).
  useEffect(() => {
    const url = new URL(location.href);
    const tok = url.searchParams.get('token');
    const rawReturn = url.searchParams.get('return') || sessionStorage.getItem('bc_return_to') || '';
    const req = sanitizeReturn(rawReturn);
    sessionStorage.removeItem('bc_return_to');
    setRequested(req);

    if (!tok) {
      // OAuth round-trip — finish immediately, no scanner risk here.
      (async () => {
        setStage('working');
        try {
          const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
          if (res.ok) {
            const body = await res.json();
            await hydrate();
            setStage('done');
            location.replace(req ?? defaultDest(body.user as UserView | undefined));
            return;
          }
        } catch {}
        setStage('error');
        setMsg('missing token');
      })();
      return;
    }
    setToken(tok);
  }, []);

  async function onConfirm() {
    if (!token || stage === 'working') return;
    setStage('working');
    try {
      const session = await verifyMagicLink(token, collectFingerprint());
      await hydrate();
      setStage('done');
      location.replace(requested ?? defaultDest(session.user));
    } catch (e: any) {
      setStage('error');
      setMsg(e?.message || 'failed');
    }
  }

  function sanitizeReturn(s: string): string | null {
    if (!s) return null;
    if (s.startsWith('/') && !s.startsWith('//')) return s;
    return null;
  }

  function defaultDest(_u: UserView | undefined): string {
    // Everyone lands on the game; admins/partners use the header menu for their
    // areas. This lets them see and test the prediction game directly.
    return localePath('/play');
  }

  // OAuth-only path or post-confirm: the simple status card stays.
  if (!token) {
    return (
      <div class="login-page">
        <div class="login-card">
          <h1>
            {stage === 'done' ? '✓' : stage === 'error' ? '…' : '⏳'} <em>BeerCup</em>
          </h1>
          <p class="lede">
            {stage === 'working' && '...'}
            {stage === 'done' && t('login.email_sent')}
            {stage === 'error' && (msg || t('login.error'))}
          </p>
          <a class="login-back" href={localePath('/login')}>← /login</a>
        </div>
      </div>
    );
  }

  return (
    <div class="login-page">
      <div class="login-card">
        <h1><em>BeerCup</em></h1>
        <p class="lede">
          {stage === 'error' ? (msg || t('login.error')) : t('login.confirm_body')}
        </p>
        <h2 style={{ margin: '12px 0 16px' }}>{t('login.confirm_title')}</h2>
        <button
          class="primary"
          onClick={onConfirm}
          disabled={stage === 'working' || stage === 'done'}
        >
          {stage === 'working' ? t('login.confirming') : t('login.confirm_cta')}
        </button>
        <a class="login-back" href={localePath('/login')}>← /login</a>
      </div>
    </div>
  );
}
